import { useEffect, useRef, useState } from 'react'
import { AlertOctagon, LayoutGrid, Phone, Play, Wrench, X } from 'lucide-react'
import { useFleet } from '../state/store'
import { BreakdownForm } from './BreakdownForm'
import { BreakdownQuickResolve } from './BreakdownQuickResolve'
import { DetailFields } from './vehicle/DetailFields'
import { useVehicleDetail } from './vehicle/useVehicleDetail'
import { pickReplayLeg, type RecentTrip } from './TripReplay'

type ReplayAvail = 'loading' | 'ready' | 'active' | 'none' | 'error'

/**
 * Compact map overlay for one vehicle. Full-page cards live in VehicleDetailPage.
 */
export function VehicleDrawer({
  vehicleId,
  onOpenFullPage,
  onReplayTrip,
}: {
  vehicleId: string
  onOpenFullPage?: () => void
  /** Replay latest completed leg for this vehicle on the map. */
  onReplayTrip?: () => void
}): JSX.Element | null {
  const close = useFleet((s) => s.selectVehicle)
  const detail = useVehicleDetail(vehicleId)
  const [form, setForm] = useState(false)
  const [replayAvail, setReplayAvail] = useState<ReplayAvail>('loading')
  const onReplayRef = useRef(onReplayTrip)
  onReplayRef.current = onReplayTrip

  useEffect(() => {
    if (!onReplayRef.current) {
      setReplayAvail('none')
      return
    }
    let cancelled = false
    setReplayAvail('loading')
    void fetch(`/api/trips/recent?limit=20&vehicle_id=${encodeURIComponent(vehicleId)}`)
      .then(async (r) => {
        const body = await r.json() as { trips?: RecentTrip[]; error?: string }
        if (cancelled) return
        if (!r.ok || body.error) {
          setReplayAvail('error')
          return
        }
        const pick = pickReplayLeg(body.trips ?? [])
        setReplayAvail(
          !pick ? 'none' : pick.kind === 'active' ? 'active' : 'ready',
        )
      })
      .catch(() => { if (!cancelled) setReplayAvail('error') })
    return () => { cancelled = true }
  }, [vehicleId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close])

  if (!detail) return null

  const { vehicle, down, state, stateColor, where, seenLabel, loadRoute, sections,
    driverPhone, driverName, trip } = detail
  const replayEnabled = replayAvail === 'ready' || replayAvail === 'active'

  return (
    <div className="absolute bottom-3 left-3 top-3 z-10 flex w-[320px] flex-col overflow-hidden rounded-sm border border-line bg-raised/95 backdrop-blur">
      <header className="flex shrink-0 items-start justify-between gap-2 border-b border-line px-3.5 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <i className="h-2 w-2 shrink-0 rounded-full" style={{ background: stateColor }} />
            <span className="truncate font-display text-sm tracking-wide">{vehicle.vehicle_id}</span>
          </div>
          <div className="mt-0.5 truncate text-2xs text-muted">
            {state} · {where ? where.name : 'in transit'} · seen {seenLabel}
          </div>
          {loadRoute && (
            <div className="mt-1 text-2xs text-muted">
              <span className={detail.load === 'Loaded' ? 'text-t1' : ''}>{loadRoute}</span>
            </div>
          )}
          {(trip?.lr_number || trip?.event || trip?.trailer) && (
            <div className="mt-0.5 truncate text-2xs text-muted">
              {trip?.lr_number ? `LR ${trip.lr_number}` : ''}
              {trip?.trailer ? `${trip?.lr_number ? ' · ' : ''}${trip.trailer}` : ''}
              {trip?.event ? `${(trip?.lr_number || trip?.trailer) ? ' · ' : ''}${trip.event}` : ''}
            </div>
          )}
          {driverName && (
            <div className="mt-0.5 truncate text-2xs text-ink/80">{driverName}</div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onOpenFullPage && (
            <button
              type="button"
              onClick={onOpenFullPage}
              className="rounded p-1 text-muted transition-colors hover:bg-high hover:text-ink"
              aria-label="Open full page details"
              title="Full page details"
            >
              <LayoutGrid size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => close(null)}
            className="rounded p-1 text-muted transition-colors hover:bg-high hover:text-ink"
            aria-label="close"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
      </header>

      {onReplayTrip && (
        <div className="shrink-0 border-b border-line px-3.5 py-2">
          <button
            type="button"
            disabled={!replayEnabled}
            onClick={() => { void onReplayRef.current?.() }}
            title={
              replayAvail === 'none'
                ? 'No replayable trip for this vehicle'
                : replayAvail === 'error'
                  ? 'Could not load trip history'
                  : replayAvail === 'loading'
                    ? 'Checking trip history…'
                    : replayAvail === 'active'
                      ? 'Replay GPS path and geotags for the active leg so far'
                      : 'Replay latest completed leg on the map'
            }
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-line bg-high px-2.5 py-1.5
                       text-[11.5px] font-medium text-ink transition-colors
                       hover:border-line-strong hover:bg-surface
                       disabled:pointer-events-none disabled:opacity-45"
          >
            <Play size={13} className="shrink-0" />
            {replayAvail === 'loading'
              ? 'Checking…'
              : replayAvail === 'active'
                ? 'Replay trip so far'
                : 'Replay latest trip'}
          </button>
          {replayAvail === 'none' && (
            <p className="mt-1 text-center text-[10px] leading-snug text-muted">
              No completed trip yet — trip still active or no GPS trail
            </p>
          )}
          {replayAvail === 'error' && (
            <p className="mt-1 text-center text-[10px] leading-snug text-muted">
              Could not load trip history
            </p>
          )}
        </div>
      )}

      {down && (
        <div className="shrink-0 border-b border-line bg-danger-dim px-3.5 py-2.5">
          <div className="flex items-center gap-1.5 text-danger">
            <AlertOctagon size={13} className="shrink-0" />
            <span className="text-[12px] font-medium">
              {down.category} · {down.severity}
            </span>
          </div>
          {down.eta_min != null && (
            <p className="mt-1 text-[11px] leading-snug text-muted">back in ~{down.eta_min} min</p>
          )}
          {/* note (editable) and who/when now live in BreakdownQuickResolve below */}
          <div className="mt-2">
            <BreakdownQuickResolve vehicleId={vehicleId} breakdown={down} compact />
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-3 overflow-auto px-3.5 py-3">
        {sections.map((section) => (
          <div key={section.id}>
            <h3 className="mb-0.5 text-[11px] font-medium leading-snug text-ink">{section.title}</h3>
            {section.subtitle && (
              <p className="mb-1.5 text-[10px] leading-snug text-muted">{section.subtitle}</p>
            )}
            {!section.subtitle && <div className="mb-1.5 border-b border-line/60" />}
            {section.body && (
              <p className="mb-1.5 text-2xs leading-relaxed text-muted">{section.body}</p>
            )}
            {section.lines && section.lines.length > 0 && (
              <ul className="mb-1.5 list-disc space-y-0.5 pl-4 text-[11px] text-muted opacity-85">
                {section.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {section.fields.length > 0 && <DetailFields fields={section.fields} />}
          </div>
        ))}
      </div>

      <footer className="flex shrink-0 items-center gap-2 border-t border-line px-3.5 py-2.5">
        <a
          href={driverPhone ? `tel:${driverPhone}` : undefined}
          aria-disabled={!driverPhone}
          className={`flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5
                      text-[11.5px] transition-colors ${
            driverPhone
              ? 'text-ink hover:bg-high'
              : 'pointer-events-none text-faint opacity-60'
          }`}
          title={driverName ?? 'no driver on the roster'}
        >
          <Phone size={12} />
          {driverName ? driverName.split(' ')[0] : 'No contact'}
        </a>
        <span className="flex-1" />
        <button
          onClick={() => setForm(true)}
          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11.5px]
                      transition-colors ${
            down
              ? 'border-[rgba(244,63,94,0.32)] bg-danger-dim text-danger'
              : 'border-line text-muted hover:bg-high hover:text-ink'
          }`}
        >
          <Wrench size={12} />
          {down ? 'Update breakdown' : 'Mark breakdown'}
        </button>
      </footer>

      {form && (
        <BreakdownForm
          vehicleId={vehicleId}
          phone={driverPhone}
          onClose={() => setForm(false)}
        />
      )}
    </div>
  )
}
